import { breadcrumbsIntegration } from '@sentry/browser';
import { init } from '@sentry/nuxt';
import { useRuntimeConfig } from 'nuxt/app';
import { filterSentryBreadcrumb } from '@sigma/common/telemetry/filterSentryBreadcrumb.js';
import { filterSentryEventData } from '@sigma/common/telemetry/filterSentryEventData.js';
import { ENV, LOCAL } from '@sigma/env/environments.js';
export function setupSentry() {
    if (LOCAL) {
        return;
    }
    const config = useRuntimeConfig().public;
    if (!config.sentry) {
        return;
    }
    // Almost every PHI channel here is closed by *not collecting*, rather than by stripping after the
    // fact: an integration that is never registered has no window in which the value exists on the
    // event and no allowlist that can drift as the SDK grows. Only `extra`/`tags` have no such
    // control, and those go through `beforeSend`.
    //
    // `breadcrumbsIntegration` is imported from `@sentry/browser`, not `@sentry/nuxt`: the latter
    // resolves to its **server** entry under the `node` condition, so the import would type-check and
    // then be `undefined` at runtime. Both are pinned to the same version, so the browser
    // bundle still ends up with a single SDK instance.
    init({
        dsn: config.sentry.dsn,
        environment: ENV,
        tunnel: `/sentry`,
        // Session replay records the patient-facing UI — chat, profile, observations — and would ship
        // that PHI to Sentry, so it stays off. Stated here rather than left to the SDK default so
        // re-enabling it has to be a deliberate, reviewable edit; masking is not an alternative.
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        // `@sentry/nuxt`'s error handler attaches `instance.$props` — whole patient objects on a
        // clinical page — to `contexts.nuxt.propsData` when this is truthy. Falsy by default; pinned
        // for the same reason as the replay zeros.
        attachProps: false,
        // Deliberately set, and `dataCollection` deliberately NOT set. Leaving `dataCollection`
        // absent makes the SDK resolve the strict branch (no user info, no request bodies, denylisted
        // cookies/headers/query); providing it *at all* switches to a looser default set, so a
        // partial `{ userInfo: false }` would quietly widen four other categories.
        sendDefaultPii: false,
        beforeBreadcrumb: filterSentryBreadcrumb,
        beforeSend: filterSentryEventData,
        integrations: (defaults) => [
            ...defaults.filter(integration => 
            // Sets `event.request` to the page URL (query string included) plus `Referer`
            // and `User-Agent`. It is the only producer of `event.request` in a browser.
            integration.name !== 'HttpContext' &&
                // Sets `contexts.culture` — locale, timezone, calendar. Environment
                // fingerprinting we have no use for.
                integration.name !== 'CultureContext' &&
                // Replaced below rather than reconfigured in place.
                integration.name !== 'Breadcrumbs'),
            breadcrumbsIntegration({
                // The verbatim argument list of every `console.*` in the app and in every dependency,
                // with no keyed structure to filter. Our own logger records its lines as breadcrumbs
                // with an already-filtered payload instead.
                console: false,
                // Repeats the previous captured error's title into the trail of every later event in
                // the session, which is how a PHI-bearing message escapes the one issue it belongs to.
                sentry: false,
                // The click trail is the most useful browser breadcrumb and the CSS selector is what
                // makes it readable, so it stays — `beforeBreadcrumb` strips the labelled attributes
                // the SDK appends. `serializeAttribute` is no alternative: it only *adds* attributes.
                dom: true,
                fetch: true,
                xhr: true,
                history: true,
            }),
        ],
    });
}
//# sourceMappingURL=setupSentry.js.map